import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
} from "@supabase/supabase-js";

export type SupabaseParsePhase = "input" | "output" | "error";

export abstract class SupabaseFunctionError extends Error {

  protected constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = new.target.name;
  }
}

const defaultResponseMessage = "Edge Function returned a non-2xx status code";
const defaultFetchMessage = "Failed to send a request to the Edge Function";

export class SupabaseResponseError<
  TBody = unknown,
> extends SupabaseFunctionError {
  readonly status: number | undefined;
  readonly body: Awaited<TBody> | undefined;
  constructor(options: {
    response?: Response;
    status?: number;
    body?: Awaited<TBody>;
    message?: string;
  }) {
    super(
      options.message ?? options.response?.statusText ?? defaultResponseMessage,
    );
    this.status = options.status ?? options.response?.status ?? undefined;
    this.body = options.body ?? undefined;
  }

  withBody<TNewBody>(body: Awaited<TNewBody>): SupabaseResponseError<TNewBody> {
    return new SupabaseResponseError<TNewBody>({
      message: this.message,
      ...(this.status !== undefined ? { status: this.status } : {}),
      body,
    });
  }
}

export class SupabaseFetchError extends SupabaseFunctionError {
  readonly context: unknown | undefined;

  constructor(options: { context?: unknown; message?: string }) {
    super(options.message ?? defaultFetchMessage);
    this.context = options.context ?? undefined;
  }
}

export class SupabaseParseError extends SupabaseFunctionError {
  readonly phase: SupabaseParsePhase;
  readonly issues: readonly unknown[] | undefined;

  constructor(options: {
    phase: SupabaseParsePhase;
    message?: string;
    issues?: readonly unknown[];
  }) {
    super(
      options.message ??
        `Failed to parse ${options.phase} for Supabase function`,
    );
    this.phase = options.phase;
    this.issues = options.issues ?? undefined;
  }
}

export type ParsedSupabaseFunctionError<TBody = { error?: string }> =
  | SupabaseResponseError<TBody>
  | SupabaseFetchError
  | SupabaseParseError;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

type FunctionsHttpOrRelayErrorLike =
  | FunctionsHttpError
  | FunctionsRelayError
  | (Record<string, unknown> & {
      name: "FunctionsHttpError" | "FunctionsRelayError";
      context?: unknown;
      message?: string;
    });

type FunctionsFetchErrorLike =
  | FunctionsFetchError
  | (Record<string, unknown> & {
      name: "FunctionsFetchError";
      context?: unknown;
      message?: string;
    });

const hasErrorName = <TName extends string>(
  value: unknown,
  expectedName: TName,
): value is Record<string, unknown> & { name: TName } =>
  isRecord(value) && typeof value["name"] === "string" && value["name"] === expectedName;

const isHttpOrRelayErrorLike = (
  error: unknown,
): error is FunctionsHttpOrRelayErrorLike =>
  error instanceof FunctionsHttpError ||
  error instanceof FunctionsRelayError ||
  hasErrorName(error, "FunctionsHttpError") ||
  hasErrorName(error, "FunctionsRelayError");

const isFetchErrorLike = (error: unknown): error is FunctionsFetchErrorLike =>
  error instanceof FunctionsFetchError || hasErrorName(error, "FunctionsFetchError");

const isResponseLike = (value: unknown): value is Response =>
  value instanceof Response ||
  (isRecord(value) &&
    typeof (value as { clone?: unknown }).clone === "function" &&
    typeof (value as { headers?: unknown }).headers === "object");

const getResponse = (context: unknown): Response | undefined =>
  isResponseLike(context)
    ? context
    : isRecord(context) && "response" in context
      ? getResponse((context as { response?: unknown }).response)
      : undefined;

const getErrorContext = (error: unknown): unknown => {
  if (
    error instanceof FunctionsHttpError ||
    error instanceof FunctionsRelayError ||
    error instanceof FunctionsFetchError
  ) {
    return error.context;
  }

  if (isRecord(error) && "context" in error) {
    return (error as { context?: unknown }).context;
  }

  return undefined;
};

const getErrorMessage = (value: unknown, fallback: string): string => {
  if (value instanceof Error) {
    return value.message;
  }

  if (typeof value === "string") {
    return value;
  }

  if (isRecord(value) && typeof value["message"] === "string") {
    return value["message"] as string;
  }

  return fallback;
};

const parseResponseBody = async <TBody>(
  response: Response,
): Promise<TBody | undefined> => {
  const clone = response.clone();
  const contentType = clone.headers.get("content-type") ?? "";

  try {
    if (contentType.includes("application/json")) {
      return (await clone.json()) as TBody;
    }

    const text = await clone.text();
    return text === "" ? undefined : (text as unknown as TBody);
  } catch {
    return undefined;
  }
};

const extractMessageFromBody = (body: unknown): string | undefined => {
  if (!isRecord(body) || !("error" in body)) return undefined;

  const message = (body as { error?: unknown }).error;
  if (typeof message === "string") {
    return message;
  }

  if (isRecord(message) && typeof message["message"] === "string") {
    return message["message"] as string;
  }

  return undefined;
};

const parseHttpOrRelayError = async <TBody>(
  error: FunctionsHttpOrRelayErrorLike,
  defaultMessage: string,
): Promise<SupabaseResponseError<TBody> | SupabaseFetchError> => {
  const response = getResponse(getErrorContext(error));
  if (!response) {
    return new SupabaseFetchError({
      context: error,
      message: getErrorMessage(error, defaultFetchMessage),
    });
  }

  const body = (await parseResponseBody<TBody>(response)) as
    | Awaited<TBody>
    | undefined;
  const message =
    extractMessageFromBody(body) ??
    error.message ??
    response.statusText ??
    defaultMessage;

  return new SupabaseResponseError<TBody>({
    response,
    ...(body !== undefined ? { body } : {}),
    message,
  });
};

export const parseSupabaseFunctionError = async <TBody = { error?: string }>(
  error: unknown,
): Promise<ParsedSupabaseFunctionError<TBody>> => {
  if (isHttpOrRelayErrorLike(error)) {
    return await parseHttpOrRelayError<TBody>(
      error,
      defaultResponseMessage,
    );
  }

  if (isFetchErrorLike(error)) {
    return new SupabaseFetchError({
      context: getErrorContext(error),
      message: getErrorMessage(error, defaultFetchMessage),
    });
  }

  return new SupabaseFetchError({
    context: getErrorContext(error) ?? error,
    message: getErrorMessage(error, defaultFetchMessage),
  });
};

const safeSerialize = (value: unknown): string => {
  if (value === undefined) {
    return "undefined";
  }

  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return "[unserializable]";
  }
};

export const formatSupabaseFunctionError = (
  error: SupabaseFunctionError,
): string => {
  const base = `${error.name}: ${error.message}`;

  if (error instanceof SupabaseResponseError) {
    const statusPart =
      error.status !== undefined ? `status ${error.status}` : "status unknown";
    const bodyPart =
      error.body !== undefined ? ` body=${safeSerialize(error.body)}` : "";
    return `${base} (${statusPart})${bodyPart}`;
  }

  if (error instanceof SupabaseFetchError) {
    const contextPart =
      error.context !== undefined
        ? ` context=${safeSerialize(error.context)}`
        : "";
    return `${base}${contextPart}`;
  }

  if (error instanceof SupabaseParseError) {
    const issuePart =
      error.issues !== undefined
        ? ` issues=${safeSerialize(error.issues)}`
        : "";
    return `${base} (phase=${error.phase})${issuePart}`;
  }

  return base;
};

export const isSupabaseResponseError = (
  error: unknown,
): error is SupabaseResponseError<unknown> =>
  error instanceof SupabaseResponseError;

export const isSupabaseFetchError = (
  error: unknown,
): error is SupabaseFetchError => error instanceof SupabaseFetchError;

export const isSupabaseParseError = (
  error: unknown,
): error is SupabaseParseError => error instanceof SupabaseParseError;

export const isSupabaseFunctionError = (
  error: unknown,
): error is SupabaseFunctionError => error instanceof SupabaseFunctionError;
